package com.algorix.web

import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement

external interface IntersectionObserverEntry {
    val target: Element
    val isIntersecting: Boolean
    val intersectionRatio: Double
}

external class IntersectionObserver(
    callback: (entries: Array<IntersectionObserverEntry>, observer: IntersectionObserver) -> Unit,
    options: dynamic = definedExternally,
) {
    fun observe(target: Element)
    fun disconnect()
}

val hasIntersectionObserver: Boolean get() = js("'IntersectionObserver' in window") as Boolean

fun media(query: String): Boolean = try {
    window.matchMedia(query).matches
} catch (_: Throwable) {
    false
}

/** Reduced motion is read once at start-up, like the reference page. */
val reduceMotion: Boolean by lazy { media("(prefers-reduced-motion: reduce)") }

fun clamp(v: Double, a: Double, b: Double): Double = if (v < a) a else if (v > b) b else v

fun Double.fx(digits: Int): String = asDynamic().toFixed(digits) as String

fun HTMLElement.cssVar(name: String, value: String?) {
    if (value == null) style.removeProperty(name) else style.setProperty(name, value)
}

/** One requestAnimationFrame loop shared by the carousel and the depth effects. */
object FrameLoop {
    private var raf = 0
    private val handlers = mutableListOf<(Double) -> Boolean>()

    /** [handler] returns true while it needs another frame. */
    fun add(handler: (Double) -> Boolean) {
        handlers += handler
    }

    fun kick() {
        if (raf == 0) raf = window.requestAnimationFrame(::tick)
    }

    private fun tick(now: Double) {
        raf = 0
        var more = false
        for (h in handlers) if (h(now)) more = true
        if (more) kick()
    }
}
